/**
 * Convierte hex (#rgb / #rrggbb) a HSL space-separated (`"H S% L%"`).
 * Compatible con el formato esperado por el branding Fiado (DEC-001 / TD-WEBAPP-007).
 */
/**
 * Valida que un string sea un HSL space-separated bien formado: `"H S% L%"`, sin NaN.
 * H ∈ [0,360], S/L ∈ [0,100] con `%`. Un branding null normaliza a `""` → aquí da false
 * para que el caller caiga al fallback (en vez de producir un hex inválido `#NaN…`).
 */
export function isValidHsl(hsl: string): boolean {
    const m = hsl.trim().match(/^(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%$/);
    if (!m) return false;
    const h = parseFloat(m[1]);
    const s = parseFloat(m[2]);
    const l = parseFloat(m[3]);
    if ([h, s, l].some((n) => Number.isNaN(n))) return false;
    return h >= 0 && h <= 360 && s >= 0 && s <= 100 && l >= 0 && l <= 100;
}

export function hslToHex(hsl: string): string {
    if (!isValidHsl(hsl)) {
        throw new Error(`hslToHex: HSL inválido "${hsl}"`);
    }
    const parts = hsl.trim().split(/\s+/);
    const h = parseFloat(parts[0] ?? '0');
    const s = parseFloat((parts[1] ?? '0%').replace('%', '')) / 100;
    const l = parseFloat((parts[2] ?? '0%').replace('%', '')) / 100;
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;
    let r = 0,
        g = 0,
        b = 0;
    if (h < 60) {
        r = c;
        g = x;
    } else if (h < 120) {
        r = x;
        g = c;
    } else if (h < 180) {
        g = c;
        b = x;
    } else if (h < 240) {
        g = x;
        b = c;
    } else if (h < 300) {
        r = x;
        b = c;
    } else {
        r = c;
        b = x;
    }
    const toHex = (n: number) =>
        Math.round((n + m) * 255)
            .toString(16)
            .padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function hexToHsl(hex: string): string {
    const cleaned = hex.replace(/^#/, '');
    const expanded =
        cleaned.length === 3
            ? cleaned
                  .split('')
                  .map((c) => c + c)
                  .join('')
            : cleaned;
    if (!/^[0-9a-fA-F]{6}$/.test(expanded)) {
        throw new Error(`hexToHsl: hex inválido "${hex}"`);
    }
    const r = parseInt(expanded.slice(0, 2), 16) / 255;
    const g = parseInt(expanded.slice(2, 4), 16) / 255;
    const b = parseInt(expanded.slice(4, 6), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    let h = 0;
    let s = 0;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
        }
        h /= 6;
    }
    const H = Math.round(h * 360);
    const S = Math.round(s * 100);
    const L = Math.round(l * 100);
    return `${String(H)} ${String(S)}% ${String(L)}%`;
}

/**
 * Conversión hex → HSL tolerante: ante hex vacío/inválido devuelve el `fallback`
 * (HSL del primario Fiado por defecto). Usada en los editores de branding (load del form
 * y submit) para que un branding sin guardar no genere valores rotos.
 */
export function safeHexToHsl(hex: string, fallback = '217 89% 51%'): string {
    try {
        return hexToHsl(hex);
    } catch {
        return fallback;
    }
}

/**
 * Conversión HSL → hex tolerante: ante HSL vacío/inválido (branding null → `""`) devuelve el
 * `fallback` hex en vez de un `#NaNNaNNaN` que el <input type=color> pintaría de negro.
 */
export function safeHslToHex(hsl: string, fallback: string): string {
    try {
        return hslToHex(hsl);
    } catch {
        return fallback;
    }
}
